import { Injectable, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState
} from '@microsoft/signalr';
import { filter, firstValueFrom } from 'rxjs';
import { environment } from '@/environments/environment';
import {
  ChatContact,
  ChatInteraction,
  ChatMessage,
  ChatMessageDeletedEvent,
  ChatMessagesReadEvent,
  ChatPresenceEvent,
  ChatThread,
  chatId,
  chatName
} from '@core/models/chat.model';
import { Endpoints } from '@utils/apiEndpoints';
import { AuthService } from './auth.service';
import { CommonService } from './common.service';
import { JwtService } from './jwt.service';
import { StorageKey, StorageService } from './storage.service';
import { TokenRefreshService } from './token-refresh.service';
import { ToastService } from './toast.service';

@Injectable({ providedIn: 'root' })
export class ChatService {
  private readonly auth = inject(AuthService);
  private readonly common = inject(CommonService);
  private readonly jwt = inject(JwtService);
  private readonly storage = inject(StorageService);
  private readonly refresh = inject(TokenRefreshService);
  private readonly router = inject(Router);
  private readonly toast = inject(ToastService);

  private hub: HubConnection | null = null;
  private studyId: string | null = null;
  private session = 0;
  private chain: Promise<void> = Promise.resolve();
  private readonly studyRoute = this.watchStudyRoute();

  readonly available = signal(false);
  readonly panelOpen = signal(false);
  readonly view = signal<'list' | 'thread'>('list');
  readonly search = signal('');
  readonly contacts = signal<ChatContact[]>([]);
  readonly isSuperAdmin = signal(false);
  readonly adminTab = signal<'employees' | 'interactions'>('employees');
  readonly interactions = signal<ChatInteraction[]>([]);
  readonly loadingInteractions = signal(false);
  readonly interaction = signal<ChatInteraction | null>(null);
  readonly showDeleted = signal(false);
  readonly messages = signal<ChatMessage[]>([]);
  readonly hasOlder = signal(false);
  readonly activeContactId = signal<string | null>(null);
  readonly onlineIds = signal<ReadonlySet<string>>(new Set());
  readonly loadingContacts = signal(false);
  readonly loadingMessages = signal(false);
  readonly loadingOlder = signal(false);
  readonly sending = signal(false);
  readonly sendError = signal('');
  readonly bottomScrollToken = signal(0);
  private readonly meId = signal('');

  readonly filteredInteractions = computed(() => {
    const query = this.search().trim().toLowerCase();
    const rows = this.interactions();
    if (!query) {
      return rows;
    }
    return rows.filter((row) =>
      `${row.firstFirstName} ${row.firstLastName} ${row.secondFirstName} ${row.secondLastName}`.toLowerCase().includes(query)
    );
  });

  readonly filteredContacts = computed(() => {
    const query = this.search().trim().toLowerCase();
    const contacts = this.contacts();
    if (!query) {
      return contacts;
    }
    return contacts.filter((contact) => chatName(contact.firstName, contact.lastName).toLowerCase().includes(query));
  });

  readonly unreadTotal = computed(() =>
    this.contacts().reduce((total, contact) => total + (contact.unreadCount || 0), 0)
  );

  readonly activeContact = computed(() => {
    const id = this.activeContactId();
    return this.contacts().find((contact) => contact.uniqueUserId === id) ?? null;
  });

  syncStudy(): void {
    const session = ++this.session;
    this.chain = this.chain.then(() => this.syncStudyInner(session)).catch(() => undefined);
  }

  disconnect(): void {
    const session = ++this.session;
    this.chain = this.chain.then(() => this.stop(session)).catch(() => undefined);
  }

  togglePanel(): void {
    const open = !this.panelOpen();
    this.panelOpen.set(open);
    if (open) {
      this.chain = this.chain
        .then(async () => {
          await this.rejoin();
          if (this.contacts().length === 0) {
            await this.loadContacts();
          }
          if (this.isSuperAdmin() && this.adminTab() === 'interactions') {
            await this.loadInteractions();
          }
        })
        .catch(() => undefined);
    }
  }

  closePanel(): void {
    this.panelOpen.set(false);
  }

  backToList(): void {
    this.view.set('list');
    this.interaction.set(null);
    this.showDeleted.set(false);
    this.sendError.set('');
  }

  setAdminTab(tab: 'employees' | 'interactions'): void {
    this.adminTab.set(tab);
    this.search.set('');
    if (tab === 'interactions') {
      this.chain = this.chain.then(() => this.loadInteractions()).catch(() => undefined);
    }
  }

  isOnline(uniqueUserId: string): boolean {
    return this.onlineIds().has(chatId(uniqueUserId));
  }

  isMine(message: ChatMessage): boolean {
    return message.senderUniqueUserId === this.meId();
  }

  isInteractionRight(message: ChatMessage): boolean {
    return message.senderUniqueUserId === this.interaction()?.secondUniqueUserId;
  }

  async openThread(uniqueUserId: string): Promise<void> {
    const id = chatId(uniqueUserId);
    this.interaction.set(null);
    this.showDeleted.set(false);
    this.activeContactId.set(id);
    this.view.set('thread');
    this.panelOpen.set(true);
    this.sendError.set('');
    this.messages.set([]);
    this.hasOlder.set(false);
    await this.loadLatest();
    if (this.activeContactId() !== id) {
      return;
    }
    await this.markRead(id);
    this.bottomScrollToken.update((token) => token + 1);
  }

  async openInteraction(row: ChatInteraction): Promise<void> {
    const pair = this.normalizeInteraction(row);
    this.interaction.set(pair);
    this.showDeleted.set(false);
    this.activeContactId.set(null);
    this.view.set('thread');
    this.panelOpen.set(true);
    this.sendError.set('');
    this.messages.set([]);
    this.hasOlder.set(false);
    await this.loadLatest();
    this.bottomScrollToken.update((token) => token + 1);
  }

  async toggleDeleted(): Promise<void> {
    if (!this.interaction()) {
      return;
    }
    this.showDeleted.update((value) => !value);
    await this.loadLatest();
  }

  async loadOlder(): Promise<void> {
    const oldest = this.messages()[0];
    const studyId = this.studyId;
    const pair = this.interaction();
    const otherId = this.activeContactId();
    if (!oldest || !studyId || this.loadingOlder() || (!pair && !otherId)) {
      return;
    }

    this.loadingOlder.set(true);
    try {
      const thread = pair
        ? await this.fetchInteractionMessages(studyId, pair, oldest.uniqueChatMessageId)
        : await this.fetchMessages(studyId, otherId!, oldest.uniqueChatMessageId);
      const existing = new Set(this.messages().map((message) => message.uniqueChatMessageId));
      const older = thread.messages.filter((message) => !existing.has(message.uniqueChatMessageId));
      this.messages.update((current) => [...older, ...current]);
      this.hasOlder.set(thread.hasOlder);
    } catch {
      this.sendError.set('Could not load earlier messages.');
    } finally {
      this.loadingOlder.set(false);
    }
  }

  async send(text: string): Promise<boolean> {
    const body = text.trim();
    const recipientId = this.activeContactId();
    const studyId = this.studyId;
    if (!body || !recipientId || this.interaction() || !studyId || !this.hub || this.sending()) {
      return false;
    }

    this.sending.set(true);
    this.sendError.set('');
    try {
      await this.hub.invoke('SendMessage', studyId, recipientId, body);
      return true;
    } catch (error) {
      this.sendError.set(this.errorText(error));
      return false;
    } finally {
      this.sending.set(false);
    }
  }

  async deleteMessages(uniqueChatMessageIds: readonly string[]): Promise<boolean> {
    const studyId = this.studyId;
    const messageIds = uniqueChatMessageIds.map((id) => chatId(id)).filter((id) => id.length > 0);
    if (!studyId || messageIds.length === 0 || !this.hub || this.hub.state !== HubConnectionState.Connected) {
      this.sendError.set('Could not delete the message.');
      return false;
    }

    this.sendError.set('');
    try {
      await this.hub.invoke('DeleteMessages', studyId, messageIds);
      return true;
    } catch (error) {
      this.sendError.set(this.errorText(error));
      return false;
    }
  }

  private watchStudyRoute(): void {
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => this.syncStudy());
    this.syncStudy();
  }

  private async syncStudyInner(session: number): Promise<void> {
    if (session !== this.session) {
      return;
    }

    if (!this.auth.isAuthenticated()) {
      await this.stop(session);
      return;
    }

    const studyId = this.readStudyId();
    if (!studyId || this.onStudyPicker()) {
      await this.listenForMessages(session);
      return;
    }

    this.meId.set(chatId(this.jwt.getUserId()));
    this.isSuperAdmin.set(this.jwt.isSuperAdmin());
    this.available.set(true);
    if (studyId === this.studyId && this.hub?.state === HubConnectionState.Connected) {
      await this.rejoin(session);
      return;
    }

    const studyChanged = this.studyId !== null && this.studyId !== studyId;
    this.studyId = studyId;
    if (studyChanged) {
      this.view.set('list');
      this.activeContactId.set(null);
      this.interaction.set(null);
      this.showDeleted.set(false);
      this.interactions.set([]);
      this.messages.set([]);
      this.hasOlder.set(false);
      this.search.set('');
    }

    const hub = await this.ensureHub(session);
    if (!hub || session !== this.session) {
      return;
    }

    await this.rejoin(session);
  }

  private async ensureHub(session: number): Promise<HubConnection | null> {
    if (this.hub && this.hub.state !== HubConnectionState.Disconnected) {
      return this.hub;
    }

    if (!this.hub) {
      this.hub = this.buildHub();
    }

    const hub = this.hub;
    await hub.start();
    if (session !== this.session) {
      return null;
    }

    return hub;
  }

  private buildHub(): HubConnection {
    const hub = new HubConnectionBuilder()
      .withUrl(this.hubUrl(), {
        accessTokenFactory: () => this.resolveAccessToken(),
        withCredentials: true
      })
      .withAutomaticReconnect()
      .build();

    hub.on('ReceiveMessage', (message: ChatMessage) => this.onMessage(message));
    hub.on('PresenceChanged', (event: ChatPresenceEvent) => this.onPresence(event));
    hub.on('MessagesRead', (event: ChatMessagesReadEvent) => this.onMessagesRead(event));
    hub.on('MessagesDeleted', (events: ChatMessageDeletedEvent[]) => this.onMessagesDeleted(events));
    hub.onreconnected(() => {
      const session = this.session;
      this.chain = this.chain
        .then(async () => {
          if (session !== this.session || this.hub !== hub) {
            return;
          }
          await this.rejoin(session);
        })
        .catch(() => undefined);
    });

    return hub;
  }

  private async rejoin(session?: number): Promise<void> {
    const hub = this.hub;
    const studyId = this.studyId;
    if (!hub || !studyId || hub.state !== HubConnectionState.Connected) {
      return;
    }
    if (session !== undefined && session !== this.session) {
      return;
    }

    const online = await hub.invoke<string[]>('JoinStudy', studyId);
    if (session !== undefined && session !== this.session) {
      return;
    }
    if (this.studyId !== studyId) {
      return;
    }
    this.onlineIds.set(new Set((online ?? []).map((id) => chatId(id))));
    await this.loadContacts();
    if (this.isSuperAdmin() && (this.adminTab() === 'interactions' || this.interaction())) {
      await this.loadInteractions();
    }
    if (this.view() === 'thread' && (this.interaction() || this.activeContactId())) {
      await this.loadLatest();
    }
    const openContactId = this.activeContactId();
    if (this.view() === 'thread' && openContactId && !this.interaction()) {
      await this.markRead(openContactId);
    }
  }

  private async listenForMessages(session: number): Promise<void> {
    if (session !== this.session) {
      return;
    }

    this.studyId = null;
    this.available.set(false);
    this.panelOpen.set(false);
    this.view.set('list');
    this.activeContactId.set(null);
    this.interaction.set(null);
    this.onlineIds.set(new Set());
    this.meId.set(chatId(this.jwt.getUserId()));

    const hub = await this.ensureHub(session);
    if (!hub || session !== this.session || hub.state !== HubConnectionState.Connected) {
      return;
    }

    try {
      await hub.invoke('Listen');
    } catch {
      // The notice can arrive on the next successful connection.
    }
  }

  private async stop(session: number): Promise<void> {
    if (session !== this.session) {
      return;
    }

    this.studyId = null;
    this.available.set(false);
    this.panelOpen.set(false);
    this.view.set('list');
    this.activeContactId.set(null);
    this.interaction.set(null);
    this.showDeleted.set(false);
    this.interactions.set([]);
    this.isSuperAdmin.set(false);
    this.contacts.set([]);
    this.messages.set([]);
    this.hasOlder.set(false);
    this.onlineIds.set(new Set());
    this.search.set('');
    this.sendError.set('');

    const hub = this.hub;
    this.hub = null;
    if (hub) {
      await hub.stop().catch(() => undefined);
    }
  }

  private async loadContacts(): Promise<void> {
    const studyId = this.studyId;
    if (!studyId) {
      return;
    }

    this.loadingContacts.set(true);
    try {
      const response = await firstValueFrom(
        this.common.doGet<ChatContact[]>(`${Endpoints.chat.contacts}?uniqueStudyId=${encodeURIComponent(studyId)}`)
      );
      if (this.studyId !== studyId) {
        return;
      }
      const rows = Array.isArray(response?.data) ? response.data : [];
      this.contacts.set(rows.map((contact) => this.normalizeContact(contact)).sort(compareContacts));
    } catch {
      if (this.studyId === studyId) {
        this.contacts.set([]);
      }
    } finally {
      if (this.studyId === studyId) {
        this.loadingContacts.set(false);
      }
    }
  }

  private async loadInteractions(): Promise<void> {
    const studyId = this.studyId;
    if (!studyId || !this.isSuperAdmin()) {
      return;
    }

    this.loadingInteractions.set(true);
    try {
      const response = await firstValueFrom(
        this.common.doGet<ChatInteraction[]>(`${Endpoints.chat.interactions}?uniqueStudyId=${encodeURIComponent(studyId)}`)
      );
      if (this.studyId !== studyId) {
        return;
      }
      const rows = Array.isArray(response?.data) ? response.data : [];
      this.interactions.set(rows.map((row) => this.normalizeInteraction(row)));
    } catch {
      if (this.studyId === studyId) {
        this.interactions.set([]);
      }
    } finally {
      if (this.studyId === studyId) {
        this.loadingInteractions.set(false);
      }
    }
  }

  private async loadLatest(): Promise<void> {
    const studyId = this.studyId;
    const pair = this.interaction();
    const otherId = this.activeContactId();
    if (!studyId || (!pair && !otherId)) {
      return;
    }

    this.loadingMessages.set(true);
    try {
      const thread = pair
        ? await this.fetchInteractionMessages(studyId, pair)
        : await this.fetchMessages(studyId, otherId!);
      const stillCurrent = pair
        ? this.samePair(this.interaction(), pair)
        : this.activeContactId() === otherId;
      if (this.studyId !== studyId || !stillCurrent) {
        return;
      }
      this.messages.set(thread.messages);
      this.hasOlder.set(thread.hasOlder);
      this.bottomScrollToken.update((token) => token + 1);
    } catch {
      if (this.studyId === studyId) {
        this.messages.set([]);
        this.hasOlder.set(false);
      }
    } finally {
      if (this.studyId === studyId) {
        this.loadingMessages.set(false);
      }
    }
  }

  private async fetchMessages(studyId: string, otherId: string, beforeId?: string): Promise<ChatThread> {
    const params = new URLSearchParams({
      uniqueStudyId: studyId,
      otherUniqueUserId: otherId,
      pageSize: '50'
    });
    if (beforeId) {
      params.set('beforeUniqueChatMessageId', beforeId);
    }

    const response = await firstValueFrom(
      this.common.doGet<ChatThread>(`${Endpoints.chat.messages}?${params.toString()}`)
    );
    const data = response?.data;
    const messages = Array.isArray(data?.messages) ? data.messages.map((message) => this.normalizeMessage(message)) : [];
    return { messages, hasOlder: Boolean(data?.hasOlder) };
  }

  private async fetchInteractionMessages(
    studyId: string,
    pair: ChatInteraction,
    beforeId?: string
  ): Promise<ChatThread> {
    const params = new URLSearchParams({
      uniqueStudyId: studyId,
      firstUniqueUserId: pair.firstUniqueUserId,
      secondUniqueUserId: pair.secondUniqueUserId,
      pageSize: '50',
      includeDeleted: String(this.showDeleted())
    });
    if (beforeId) {
      params.set('beforeUniqueChatMessageId', beforeId);
    }

    const response = await firstValueFrom(
      this.common.doGet<ChatThread>(`${Endpoints.chat.interactionMessages}?${params.toString()}`)
    );
    const data = response?.data;
    const messages = Array.isArray(data?.messages) ? data.messages.map((message) => this.normalizeMessage(message)) : [];
    return { messages, hasOlder: Boolean(data?.hasOlder) };
  }

  private async markRead(otherId: string): Promise<void> {
    const hub = this.hub;
    const studyId = this.studyId;
    if (!hub || !studyId || hub.state !== HubConnectionState.Connected) {
      return;
    }

    this.contacts.update((contacts) =>
      contacts.map((contact) => (contact.uniqueUserId === otherId ? { ...contact, unreadCount: 0 } : contact))
    );

    try {
      await hub.invoke('MarkRead', studyId, otherId);
    } catch {
      // The thread is already open. A later refresh will reconcile the unread count.
    }
  }

  private onMessage(raw: ChatMessage): void {
    const message = this.normalizeMessage(raw);
    if (!message.uniqueChatMessageId) {
      return;
    }
    if (message.uniqueStudyId !== this.studyId) {
      if (message.recipientUniqueUserId === this.meId() && message.senderUniqueUserId !== this.meId()) {
        const name = chatName(message.senderFirstName, message.senderLastName) || 'Someone';
        const study = message.studyName?.trim() || 'another study';
        this.toast.info(`${name} sent you a message on ${study}.`);
      }
      return;
    }
    if (this.messages().some((item) => item.uniqueChatMessageId === message.uniqueChatMessageId)) {
      this.refreshInteractions();
      return;
    }

    const involvesMe =
      message.senderUniqueUserId === this.meId() || message.recipientUniqueUserId === this.meId();
    if (involvesMe) {
      const mine = message.senderUniqueUserId === this.meId();
      const otherId = mine ? message.recipientUniqueUserId : message.senderUniqueUserId;
      const viewing =
        this.panelOpen() && this.view() === 'thread' && !this.interaction() && this.activeContactId() === otherId;

      if (viewing) {
        this.messages.update((current) => [...current, message]);
        this.bottomScrollToken.update((token) => token + 1);
        if (!mine) {
          void this.markRead(otherId);
        }
      }

      this.patchContact(otherId, message, viewing ? 'clear' : mine ? 'keep' : 'increment');
    }

    if (this.matchesOpenInteraction(message.senderUniqueUserId, message.recipientUniqueUserId)) {
      if (!this.messages().some((item) => item.uniqueChatMessageId === message.uniqueChatMessageId)) {
        this.messages.update((current) => [...current, message]);
        this.bottomScrollToken.update((token) => token + 1);
      }
    }

    this.refreshInteractions();
  }

  private onPresence(event: ChatPresenceEvent): void {
    const studyId = chatId(event?.uniqueStudyId ?? event?.UniqueStudyId);
    if (studyId && studyId !== this.studyId) {
      return;
    }

    const id = chatId(event?.uniqueUserId ?? event?.UniqueUserId);
    if (!id) {
      return;
    }
    const online = event?.isOnline ?? event?.IsOnline ?? false;
    const next = new Set(this.onlineIds());
    if (online) {
      next.add(id);
    } else {
      next.delete(id);
    }
    this.onlineIds.set(next);
  }

  private onMessagesDeleted(raw: ChatMessageDeletedEvent[]): void {
    const removed = (Array.isArray(raw) ? raw : []).filter((item) => this.applyMessageDeleted(item));
    if (removed.length === 0) {
      return;
    }

    this.chain = this.chain.then(() => this.loadContacts()).catch(() => undefined);
    this.refreshInteractions();
  }

  private applyMessageDeleted(raw: ChatMessageDeletedEvent): boolean {
    const studyId = chatId(raw?.uniqueStudyId);
    const messageId = chatId(raw?.uniqueChatMessageId);
    if (!messageId || studyId !== this.studyId) {
      return false;
    }

    const senderId = chatId(raw.senderUniqueUserId);
    const recipientId = chatId(raw.recipientUniqueUserId);
    if (this.matchesOpenInteraction(senderId, recipientId) && this.showDeleted()) {
      this.messages.update((messages) =>
        messages.map((message) =>
          message.uniqueChatMessageId === messageId ? { ...message, isDeleted: true } : message
        )
      );
    } else {
      this.messages.update((messages) => messages.filter((message) => message.uniqueChatMessageId !== messageId));
    }
    return true;
  }

  private onMessagesRead(event: ChatMessagesReadEvent): void {
    if (chatId(event?.uniqueStudyId) !== this.studyId) {
      return;
    }

    const readerId = chatId(event.readerUniqueUserId);
    const otherId = chatId(event.otherUniqueUserId);
    if (readerId === this.meId()) {
      this.contacts.update((contacts) =>
        contacts.map((contact) => (contact.uniqueUserId === otherId ? { ...contact, unreadCount: 0 } : contact))
      );
    }

    if (otherId === this.meId() && this.activeContactId() === readerId) {
      const readAt = new Date().toISOString();
      this.messages.update((messages) =>
        messages.map((message) =>
          message.senderUniqueUserId === this.meId() && !message.readDate
            ? { ...message, readDate: readAt }
            : message
        )
      );
    }
  }

  private patchContact(otherId: string, message: ChatMessage, unread: 'clear' | 'increment' | 'keep'): void {
    const existing = this.contacts().find((contact) => contact.uniqueUserId === otherId);
    if (!existing) {
      this.chain = this.chain.then(() => this.loadContacts()).catch(() => undefined);
      return;
    }

    this.contacts.update((contacts) =>
      contacts
        .map((contact) => {
          if (contact.uniqueUserId !== otherId) {
            return contact;
          }
          const unreadCount =
            unread === 'clear' ? 0 : unread === 'increment' ? contact.unreadCount + 1 : contact.unreadCount;
          return {
            ...contact,
            lastMessage: message.messageText,
            lastMessageDate: message.sentDate,
            unreadCount
          };
        })
        .sort(compareContacts)
    );
  }

  private normalizeContact(contact: ChatContact): ChatContact {
    return {
      uniqueUserId: chatId(contact.uniqueUserId),
      firstName: contact.firstName ?? '',
      lastName: contact.lastName ?? '',
      photo: contact.photo?.trim() ? contact.photo : null,
      lastMessage: contact.lastMessage ?? null,
      lastMessageDate: contact.lastMessageDate ?? null,
      unreadCount: Number(contact.unreadCount) || 0
    };
  }

  private normalizeMessage(message: ChatMessage): ChatMessage {
    return {
      uniqueStudyId: chatId(message.uniqueStudyId),
      uniqueChatMessageId: chatId(message.uniqueChatMessageId),
      senderUniqueUserId: chatId(message.senderUniqueUserId),
      senderFirstName: message.senderFirstName ?? '',
      senderLastName: message.senderLastName ?? '',
      recipientUniqueUserId: chatId(message.recipientUniqueUserId),
      studyName: message.studyName?.trim() || '',
      messageText: message.messageText ?? '',
      sentDate: message.sentDate,
      readDate: message.readDate ?? null,
      isDeleted: Boolean(message.isDeleted)
    };
  }

  private normalizeInteraction(row: ChatInteraction): ChatInteraction {
    const people = [
      {
        uniqueUserId: chatId(row.firstUniqueUserId),
        firstName: row.firstFirstName ?? '',
        lastName: row.firstLastName ?? ''
      },
      {
        uniqueUserId: chatId(row.secondUniqueUserId),
        firstName: row.secondFirstName ?? '',
        lastName: row.secondLastName ?? ''
      }
    ].sort((left, right) =>
      chatName(left.firstName, left.lastName).localeCompare(chatName(right.firstName, right.lastName), undefined, {
        sensitivity: 'base'
      })
    );
    const [first, second] = people;

    return {
      firstUniqueUserId: first.uniqueUserId,
      firstFirstName: first.firstName,
      firstLastName: first.lastName,
      secondUniqueUserId: second.uniqueUserId,
      secondFirstName: second.firstName,
      secondLastName: second.lastName,
      lastMessage: row.lastMessage ?? null,
      lastMessageDate: row.lastMessageDate ?? null,
      lastMessageIsDeleted: Boolean(row.lastMessageIsDeleted)
    };
  }

  private samePair(left: ChatInteraction | null, right: ChatInteraction | null): boolean {
    if (!left || !right) {
      return false;
    }
    return left.firstUniqueUserId === right.firstUniqueUserId && left.secondUniqueUserId === right.secondUniqueUserId;
  }

  private matchesOpenInteraction(senderId: string, recipientId: string): boolean {
    const pair = this.interaction();
    if (!pair || !this.panelOpen() || this.view() !== 'thread') {
      return false;
    }
    const ids = [pair.firstUniqueUserId, pair.secondUniqueUserId];
    return ids.includes(senderId) && ids.includes(recipientId);
  }

  private refreshInteractions(): void {
    if (!this.isSuperAdmin() || !this.panelOpen()) {
      return;
    }
    if (this.adminTab() !== 'interactions' && !this.interaction()) {
      return;
    }
    this.chain = this.chain.then(() => this.loadInteractions()).catch(() => undefined);
  }

  private onStudyPicker(): boolean {
    const path = this.router.url.split(/[?#]/)[0];
    return path === '/study' || path === '/add-edit-study';
  }

  private readStudyId(): string | null {
    const value = this.storage.getValue(StorageKey.uniqueStudyId);
    if (!value || value === 'null') {
      return null;
    }
    return chatId(String(value));
  }

  private async resolveAccessToken(): Promise<string> {
    const current = this.auth.getAccessToken();
    const exp = Number(this.jwt.getClaims(current)?.exp ?? 0);
    const remainingMs = exp * 1000 - Date.now();
    if (current && remainingMs > 2 * 60 * 1000) {
      return current;
    }

    const refreshed = await firstValueFrom(this.refresh.refreshAccessToken());
    if (refreshed) {
      this.auth.setToken(refreshed);
      return refreshed;
    }

    return current ?? '';
  }

  private hubUrl(): string {
    const origin = (environment.apiOrigin || environment.baseUrl || '')
      .replace(/\/+$/, '')
      .replace(/\/api\/admin$/i, '');
    return `${origin}/hubs/chat`;
  }

  private errorText(error: unknown): string {
    if (error instanceof Error && error.message) {
      return error.message.replace(/^Error:\s*/, '');
    }
    return 'Could not send the message.';
  }
}

function compareContacts(left: ChatContact, right: ChatContact): number {
  const leftTime = left.lastMessageDate ? new Date(left.lastMessageDate).getTime() : 0;
  const rightTime = right.lastMessageDate ? new Date(right.lastMessageDate).getTime() : 0;
  if (leftTime !== rightTime) {
    return rightTime - leftTime;
  }
  return chatName(left.firstName, left.lastName).localeCompare(chatName(right.firstName, right.lastName));
}
